/**
 * Shared recipe import from URL: POST /recipes/import and optional task polling.
 * Used by chat (App.jsx) and Saved Lists Add Recipe modal (SavedListsManager.jsx).
 */
import { authenticatedFetch } from './auth.js'

const POLL_INTERVAL_MS = 250
const POLL_MAX_ATTEMPTS = 80 // ~20s max

export const RECIPE_IMPORT_SUCCESS_STATUSES = new Set(['created', 'updated', 'deduplicated'])

export function isSuccessfulRecipeImport(result) {
  return RECIPE_IMPORT_SUCCESS_STATUSES.has(result?.status)
}

export function formatRecipeReimportMessage(result) {
  const name = String(result?.name || '').trim() || 'This recipe'
  if (result?.status === 'deduplicated') {
    return {
      title: `“${name}” already saved`,
      message: 'No new details to add from this source.',
    }
  }
  if (result?.status !== 'updated') {
    return {
      title: `“${name}” imported`,
      message: 'Saved from the recipe source.',
    }
  }

  const delta = result?.delta && typeof result.delta === 'object' ? result.delta : {}
  const parts = []
  const addedIngredients = Array.isArray(delta.added_ingredients) ? delta.added_ingredients.length : 0
  const updatedIngredients = Array.isArray(delta.updated_ingredients) ? delta.updated_ingredients.length : 0
  const addedMetadata = Array.isArray(delta.added_metadata) ? delta.added_metadata : []
  if (addedIngredients) {
    parts.push(`${addedIngredients} ingredient${addedIngredients === 1 ? '' : 's'}`)
  }
  if (updatedIngredients) {
    parts.push(`measures for ${updatedIngredients} ingredient${updatedIngredients === 1 ? '' : 's'}`)
  }
  if (addedMetadata.includes('instructions') || addedMetadata.includes('cook_steps')) {
    parts.push('cooking steps')
  }
  if (delta.description_updated) {
    parts.push('the description')
  }
  const leftover = addedMetadata.filter((key) => !['instructions', 'cook_steps'].includes(key))
  if (leftover.length && parts.length === 0) {
    parts.push('missing details')
  }
  return {
    title: `“${name}” updated`,
    message: parts.length
      ? `Added ${parts.join(', ')} from the source.`
      : 'Filled in missing details from the source.',
  }
}

/**
 * Poll a task until completed, failed, or cancelled.
 * @param {string} taskId
 * @param {(progress: number, message?: string) => void} [onUpdate]
 * @returns {Promise<{ status: string, result?: object, error?: string }>}
 */
export async function pollTask(taskId, onUpdate) {
  for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
    const r = await authenticatedFetch(`/tasks/${encodeURIComponent(taskId)}`)
    const responseText = await r.text()
    const contentType = r.headers.get('content-type') || ''
    const isJson = contentType.includes('application/json')

    let d = {}
    if (isJson) {
      try {
        d = JSON.parse(responseText)
      } catch {
        return { status: 'failed', error: `Invalid JSON response: ${responseText.substring(0, 200)}` }
      }
    } else {
      if (responseText.includes('<!doctype') || responseText.includes('react-refresh')) {
        return { status: 'failed', error: 'Backend server may not be running or /tasks not proxied correctly. Received HTML instead of JSON.' }
      }
      return { status: 'failed', error: `Server returned non-JSON response: ${responseText.substring(0, 200)}` }
    }

    if (d.status === 'completed' || d.status === 'failed' || d.status === 'cancelled') {
      return d
    }
    if (onUpdate && d.progress != null) {
      onUpdate(d.progress, d.metadata?.progress_message)
    }
    await new Promise(res => setTimeout(res, POLL_INTERVAL_MS))
  }
  return { status: 'failed', error: 'Timeout waiting for task' }
}

function parseImportResponse(res, responseText) {
  const contentType = res.headers.get('content-type') || ''
  const isJson = contentType.includes('application/json')

  let data = {}
  if (isJson) {
    try {
      data = JSON.parse(responseText)
    } catch {
      throw new Error(`Invalid JSON response from server: ${responseText.substring(0, 200)}`)
    }
  } else {
    if (responseText.includes('<!doctype') || responseText.includes('react-refresh')) {
      throw new Error('Backend server may not be running or request was not proxied correctly. Received HTML instead of JSON.')
    }
    throw new Error(`Server returned unexpected content type: ${contentType}. Response: ${responseText.substring(0, 200)}`)
  }
  return data
}

function isAuthError(msg) {
  const s = (msg || '').toLowerCase()
  return (
    s.includes('401') || s.includes('403') ||
    s.includes('unauthorized') || s.includes('forbidden') ||
    s.includes('authentication required') || s.includes('requires authentication')
  )
}

/**
 * True when the error is about the external recipe site requiring login (e.g. NYT, Food & Wine).
 * We must NOT show the app login modal for these — show the recipe-site message instead.
 */
function isRecipeSiteAuthError(msg) {
  const s = (msg || '')
  return (
    /recipe site|the site .* requires|🔒 authentication required:\s*the recipe site/i.test(s)
  )
}

/** Only true when our app requires login (JWT missing/invalid), not the recipe site. */
function isAppAuthRequired(httpStatus, errorMsg) {
  if (httpStatus === 401 || httpStatus === 403) return true
  return isAuthError(errorMsg) && !isRecipeSiteAuthError(errorMsg)
}

/** Exported for use in App.jsx catch block: avoid showing app login for recipe-site auth errors. */
export function isRecipeSiteAuthErrorMsg(msg) {
  return isRecipeSiteAuthError(msg)
}

/**
 * Parse domain from recipe-site auth error message.
 * Backend messages look like: "This recipe site (foodandwine.com) requires authentication..."
 * @param {string} errorMessage
 * @returns {string|null} domain (e.g. 'foodandwine.com') or null
 */
export function parseRecipeSiteAuthDomain(errorMessage) {
  if (!errorMessage || typeof errorMessage !== 'string') return null
  const m = errorMessage.match(/recipe site\s*\(([^)]+)\)/i) || errorMessage.match(/site\s+'([^']+)'\s+requires/i)
  if (m && m[1]) return m[1].trim().replace(/^www\./, '')
  return null
}

/**
 * Import a recipe from URL. Handles sync response and async task polling.
 * Dispatches 'recipeImported' on success so Saved Lists can refresh.
 *
 * @param {object} intent - { title, url?, author?, include_pantry_items?, tags? }
 * @param {(progress: number, message?: string) => void} [onProgress] - Called during polling
 * @returns {Promise<{ success: boolean, result?: object, error?: string, requiresAuth?: boolean }>}
 */
export async function importRecipeFromUrl(intent, onProgress) {
  try {
    const res = await authenticatedFetch('/recipes/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(intent)
    })
    const responseText = await res.text()
    const data = parseImportResponse(res, responseText)

    if (!res.ok) {
      const errorMsg = data.detail || data.error || `HTTP ${res.status}`
      return {
        success: false,
        error: errorMsg,
        requiresAuth: isAppAuthRequired(res.status, errorMsg)
      }
    }

    // Sync completion
    if (data.status && (data.status === 'completed' || data.status === 'failed' || data.result)) {
      const result = data.result || {}
      const ok = isSuccessfulRecipeImport(result)
      if (ok) {
        window.dispatchEvent(new CustomEvent('recipeImported', { detail: { listId: result.id } }))
        return { success: true, result }
      }
      const errorMsg = data.error || result.error || 'Unknown error'
      return {
        success: false,
        error: errorMsg,
        requiresAuth: isAppAuthRequired(200, errorMsg)
      }
    }

    // Async: poll task
    const taskId = data.task_id
    if (!taskId) {
      return { success: false, error: 'Server did not return a task ID' }
    }
    const done = await pollTask(taskId, onProgress)
    const result = done.result || {}
    if (done.status === 'completed' && isSuccessfulRecipeImport(result)) {
      window.dispatchEvent(new CustomEvent('recipeImported', { detail: { listId: result.id } }))
      return { success: true, result }
    }
    const errorMsg = done.error || result.error || 'Unknown error'
    return {
      success: false,
      error: errorMsg,
      requiresAuth: isAppAuthRequired(200, errorMsg)
    }
  } catch (e) {
    const errorMsg = e.message || 'Unknown error'
    return {
      success: false,
      error: errorMsg,
      requiresAuth: isAppAuthRequired(0, errorMsg)
    }
  }
}

/**
 * Build a minimal import intent from a URL string (e.g. from Saved Lists modal).
 * @param {string} url
 * @returns {{ title: string, url: string, include_pantry_items: boolean, tags: [] }}
 */
export function buildIntentFromUrl(url) {
  const u = (url || '').trim()
  let title = 'Imported Recipe'
  try {
    const urlObj = new URL(u)
    const hostname = urlObj.hostname.replace(/^www\./, '')
    title = `Recipe from ${hostname}`
  } catch {
    // keep default
  }
  return {
    title,
    url: u,
    include_pantry_items: false,
    tags: []
  }
}

/**
 * Import a recipe from pasted text via POST /recipes/import_text (sync).
 * @param {string} text
 * @returns {Promise<{ success: boolean, result?: object, error?: string, missingFields?: string[], quality?: object, qualityHint?: string }>}
 */
export async function importRecipeFromText(text) {
  const body = {
    text: (text || '').trim(),
    include_pantry_items: false,
    tags: ['recipe'],
  }
  try {
    const res = await authenticatedFetch('/recipes/import_text', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const responseText = await res.text()
    let data = {}
    try {
      data = JSON.parse(responseText)
    } catch {
      return { success: false, error: `Invalid server response: ${responseText.substring(0, 200)}` }
    }

    if (res.status === 422) {
      const detail = data.detail && typeof data.detail === 'object' && !Array.isArray(data.detail)
        ? data.detail
        : (typeof data === 'object' && data ? data : {})
      const missing = Array.isArray(detail.missing_fields)
        ? detail.missing_fields
        : (Array.isArray(detail.missingFields) ? detail.missingFields : [])
      const labels = {
        title: 'a recipe title',
        ingredients: 'enough ingredients',
        instructions: 'clear cooking steps',
        cuisine: 'a cuisine tag',
        time: 'prep or cook time',
        time_or_servings: 'prep or cook time',
        confidence: 'a clearer paste (parse confidence was low)',
        instructions_incomplete: 'complete directions (plating/assembly)',
      }
      const human = missing.map((m) => labels[m] || String(m)).filter(Boolean).join(', ')
      const baseMessage = typeof detail.message === 'string' && detail.message.trim()
        ? detail.message.trim()
        : 'This paste is missing fields needed for filtering and cooking.'
      const friendly = human
        ? `${baseMessage} Please include ${human}.`
        : baseMessage
      return {
        success: false,
        error: friendly,
        missingFields: missing,
        quality: detail.quality && typeof detail.quality === 'object' ? detail.quality : undefined,
        qualityHint: human ? `Missing: ${human}.` : undefined,
      }
    }

    if (!res.ok) {
      const detail = data.detail
      let msg = ''
      if (typeof detail === 'string') msg = detail
      else if (detail && typeof detail === 'object' && typeof detail.message === 'string') msg = detail.message
      else if (typeof data.message === 'string') msg = data.message
      else if (typeof data.error === 'string') msg = data.error
      else msg = `Import failed (${res.status})`
      return { success: false, error: msg }
    }

    if (data.status === 'created' || data.status === 'deduplicated') {
      window.dispatchEvent(new CustomEvent('recipeImported', {
        detail: {
          listId: data.id,
          name: data.name,
          source: 'paste',
          scroll: false,
        },
      }))
      return { success: true, result: data }
    }

    return { success: false, error: data.message || 'Unexpected import response.' }
  } catch (e) {
    return { success: false, error: e.message || 'Unknown error' }
  }
}
